import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { hasPerm, moduleOn } from "@/lib/perms";
import { inr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { useCountUp } from "./_shared";
import { InfoHint } from "./InfoHint";
import { Sun, Sunrise, Moon, TrendingUp, TrendingDown } from "lucide-react";

const PERIODS = [
  { k: "today", label: "Today", note: "Operational" },
  { k: "week", label: "This Week", note: "Performance" },
  { k: "month", label: "This Month", note: "Growth" },
];

// Hero period -> dashboard drill-down preset understood by destination lists.
const DRILL_PERIOD = { today: "today", week: "this_week", month: "this_month" };
const linkCls = "cursor-pointer rounded-2xl transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70";

function greetWord() {
  const h = new Date().getHours();
  if (h < 12) return { t: "Good morning", I: Sunrise };
  if (h < 17) return { t: "Good afternoon", I: Sun };
  return { t: "Good evening", I: Moon };
}

// Whole-block link without nesting buttons (inner InfoHint renders as span when clickable).
function HeroLink({ clickable, onOpen, label, testid, className = "", children }) {
  if (!clickable) return <div data-testid={testid}>{children}</div>;
  const onKey = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); }
  };
  return (
    <div role="link" tabIndex={0} aria-label={label} onClick={onOpen} onKeyDown={onKey} data-testid={testid}
      className={cn(linkCls, "-mx-3 px-3 pb-2 pt-0.5", className)}>{children}</div>
  );
}

export function BusinessGreeting({ period, onPeriod }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canPayments = hasPerm(user, "payments.view") && moduleOn(user, "payments");
  const canBookings = hasPerm(user, "bookings.view") && moduleOn(user, "bookings");
  const canCalendar = hasPerm(user, "bookings.view") && moduleOn(user, "calendar");
  const canInvoices = hasPerm(user, "invoices.view") && moduleOn(user, "invoices");
  const periodWord = period === "today" ? "today" : period === "week" ? "this week" : "this month";
  const dash = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });
  const sum = useQuery({ queryKey: ["daily-summary"], queryFn: async () => (await api.get("/dashboard/daily-summary")).data });

  const rev = dash.data?.revenue;
  const sales = dash.data?.sales;
  const bk = dash.data?.bookings;
  const collected = period === "month" ? rev?.month : period === "week" ? rev?.week : rev?.today;
  const soldFig = period === "month" ? sales?.month : period === "week" ? sales?.week : sales?.today;
  const animatedSales = useCountUp(typeof soldFig === "number" ? soldFig : 0);
  const animatedCol = useCountUp(typeof collected === "number" ? collected : 0);
  const { t, I } = greetWord();
  const first = user?.name?.split(" ")[0] || "there";

  // today-vs-yesterday comparison (collections, real)
  const yrev = sum.data?.yesterday_revenue;
  let cmp = null;
  if (period === "today" && typeof collected === "number" && typeof yrev === "number" && yrev > 0) {
    cmp = Math.round(((collected - yrev) / yrev) * 1000) / 10;
  }
  const monthCmp = period === "month" && typeof rev?.delta_pct === "number" ? rev.delta_pct : null;
  const delta = period === "today" ? cmp : monthCmp;

  return (
    <div className="relative overflow-hidden rounded-3xl bg-slate-900 text-white p-6 sm:p-8 shadow-floating" data-testid="business-hero">
      <div className="pointer-events-none absolute inset-0 opacity-30"
        style={{ backgroundImage: "radial-gradient(circle at 88% 8%, #9E86FD 0, transparent 38%), radial-gradient(circle at 10% 90%, #5837D8 0, transparent 42%)" }} />
      <div className="relative">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-pawkt-primary-light text-sm font-semibold">
              <I className="h-4 w-4" /> {t}, {first}
            </div>
            <h1 className="font-display text-2xl sm:text-[28px] font-extrabold tracking-tight mt-1">
              Here's what's happening in your business.
            </h1>
            <p className="text-slate-300 text-sm mt-1">{sum.data?.greeting || "Your business, alive."}</p>
          </div>
          {/* revenue period context */}
          <div className="flex flex-col items-start sm:items-end gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Collections period</span>
            <div className="inline-flex rounded-full bg-white/10 p-1 backdrop-blur-sm" data-testid="time-selector" role="tablist" aria-label="Collections period">
              {PERIODS.map((p) => (
                <button key={p.k} onClick={() => onPeriod(p.k)} data-testid={`period-${p.k}`} role="tab" aria-selected={period === p.k}
                  className={cn("rounded-full px-3.5 py-1.5 text-xs font-bold transition-all",
                    period === p.k ? "bg-white text-pawkt-primary-deep shadow-card" : "text-slate-300 hover:text-white")}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            {/* PRIMARY — Today's Sales: business/service/sale date (never payment date). Opens Invoices for
                the period; invoice totals may differ (sales also count services by service date + memberships). */}
            <HeroLink clickable={canInvoices} testid="hero-sales-link" onOpen={() => navigate(`/invoices?period=${DRILL_PERIOD[period] || "today"}`)}
              label={`Open invoices for ${periodWord}`}>
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400" data-testid="hero-sales-label">
              {period === "today" ? "Today's sales" : period === "week" ? "Sold this week" : "Sold this month"}
              <span className="normal-case tracking-normal font-medium text-slate-500">· based on service/sale date</span>
              <InfoHint
                as={canInvoices ? "span" : "button"}
                className="text-slate-400 hover:text-white"
                testid="hero-sales-info"
                label="Why is this based on service/sale date?"
                text="Based on service/sale date — shows business sold or services completed today." />
            </div>
            <div className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight tabular mt-1" data-testid="hero-sales">
              {inr(Math.round(animatedSales))}
            </div>
            </HeroLink>
            {/* SECONDARY — Collected: payment date (whole block opens Payments for the period) */}
            <HeroLink clickable={canPayments} testid="hero-collected" onOpen={() => navigate(`/payments?period=${DRILL_PERIOD[period] || "today"}`)}
              label={`Open payments collected ${periodWord}`}>
            <div className="mt-4 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400" data-testid="hero-revenue-label">
              Collected {period === "today" ? "today" : period === "week" ? "this week" : "this month"}
              <span className="normal-case tracking-normal font-medium text-slate-500">· based on payment date</span>
              <InfoHint
                as={canPayments ? "span" : "button"}
                className="text-slate-400 hover:text-white"
                testid="hero-revenue-info"
                label="Why is this based on payment date?"
                text="Based on payment date — shows payments collected today." />
            </div>
            <div className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight tabular mt-1 text-slate-200" data-testid="hero-revenue">
              {inr(Math.round(animatedCol))}
            </div>
            {delta !== null && delta !== undefined && (
              <div className={cn("mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold",
                delta >= 0 ? "bg-pawkt-success-deep/20 text-pawkt-success" : "bg-pawkt-emergency/20 text-pawkt-emergency")}>
                {delta >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                {Math.abs(delta)}% {period === "today" ? "vs yesterday" : "vs last month"} collected
              </div>
            )}
            </HeroLink>
          </div>

          {bk && (
            <div className="grid grid-cols-3 gap-3" data-testid="hero-booking-signals">
              {[
                ["today", "Today", bk.today, canBookings && "/bookings?period=today", "Open today's bookings"],
                ["upcoming", "Upcoming", bk.upcoming, canCalendar && "/calendar", "Open the calendar"],
                ["completed", "Completed", bk.completed, canBookings && "/bookings?status=completed", "Open completed bookings"],
              ].map(([k, l, v, to, aria]) => {
                const body = (<><div className="font-display text-xl font-extrabold tabular">{v ?? 0}</div><div className="text-[11px] text-slate-300">{l}</div></>);
                const cls = "rounded-2xl bg-white/10 px-4 py-3 text-center backdrop-blur-sm min-w-[84px]";
                return to ? (
                  <button key={k} type="button" onClick={() => navigate(to)} aria-label={`${aria} (${v ?? 0})`} data-testid={`hero-bk-${k}`}
                    className={cn(cls, "cursor-pointer transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70")}>{body}</button>
                ) : <div key={k} className={cls} data-testid={`hero-bk-${k}`}>{body}</div>;
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
